import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { enqueueSnackbar } from 'notistack';

import { getResolvedEndpoint } from '@/api/utils/getResolvedEndpoint';
import { handleApiCall } from '@/api/utils/handleApiCall';
import { MutationStateFns } from '@/api/utils/mutation';
import { useAuth } from '@/context/AuthProvider';

import { QUERY_KEY as CONVERSATION_DETAILS_QUERY_KEY } from './useGetConversationDetails';

export type ApiRequest_SendMessage_Conversation = {
  conversation_id: string;
  content: string;
};

type RequestBody = Pick<ApiRequest_SendMessage_Conversation, 'content'>;

export type ApiResponse_SendMessage_Conversation = {
  id: number;
  external_id: string;
  content: string;
  body: string;
  sender_name: string;
  sender: string;
  participant_sid: string | null;
  created_at: string;
  conversation_id: string;
};

const METHOD = 'POST';
const ENDPOINT = 'conversation/:id/send';

export const useSendMessageToConversation = (
  stateFns?: MutationStateFns<ApiResponse_SendMessage_Conversation>
) => {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ApiRequest_SendMessage_Conversation) =>
      handleApiCall<ApiResponse_SendMessage_Conversation, RequestBody>(
        session,
        METHOD,
        getResolvedEndpoint(ENDPOINT, { id: data.conversation_id }),
        { content: data.content }
      ),
    onSuccess: (data, variables) => {
      enqueueSnackbar('Message sent successfully', { variant: 'success' });
      queryClient.invalidateQueries({
        queryKey: CONVERSATION_DETAILS_QUERY_KEY({ conversation_id: variables.conversation_id }),
        refetchType: 'all',
      });

      stateFns?.onSuccess?.(data);
    },
    onError: (err: AxiosError) => {
      enqueueSnackbar('Failed to send message', { variant: 'error' });
      stateFns?.onError?.(err);
    },
  });
};
